import React, { useState } from 'react';
import { Play, Send, RotateCcw } from 'lucide-react';
import { formatRecordingDuration } from '../utils/formatRecordingDuration';
import ConfirmDialog from './ConfirmDialog';

interface BrowserLocalRecordingRecoveryPromptProps {
  title: string;
  description: string;
  continueLabel?: string;
  restoreLabel?: string;
  discardLabel?: string;
  headingLevel?: 2 | 3;
  titleClassName?: string;
  descriptionClassName?: string;
  onRestore: () => void;
  onContinue?: () => void;
  onDiscard: () => void;
  durationMs?: number;
  className?: string;
}

// Each option is a full-width row rather than a column: the three stacked
// cards were squeezed into the page's left column, wrapping their labels.
const rowClassName =
  'flex flex-col gap-3 rounded-xl bg-white p-4 sm:flex-row sm:items-center sm:gap-6';
// Wide enough for the longest label to stay on one line, and left-aligned so
// the three read as a list of choices rather than three centred blocks.
const actionClassName =
  'inline-flex w-full items-center justify-start gap-3 rounded-xl px-5 py-3 text-left text-base font-semibold transition-colors sm:w-[24rem] sm:shrink-0';
const explainerClassName = 'text-base text-brand-black sm:flex-1';

const BrowserLocalRecordingRecoveryPrompt: React.FC<BrowserLocalRecordingRecoveryPromptProps> = ({
  title,
  description,
  continueLabel = 'Ga door met het gepauzeerde gesprek',
  restoreLabel = 'Ik was klaar, verstuur het om te verwerken',
  discardLabel = 'Begin opnieuw',
  headingLevel = 3,
  titleClassName = 'text-xl font-semibold text-brand-red mb-2',
  descriptionClassName = 'text-base text-brand-blauw mb-5',
  onRestore,
  onContinue,
  onDiscard,
  durationMs,
  className = 'mb-8 rounded-2xl border border-brand-border bg-brand-secondary p-6 text-left',
}) => {
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const recordedDuration = formatRecordingDuration(durationMs);
  // HomePage cannot continue a take, so it genuinely offers one option fewer.
  const optionCount = onContinue ? 3 : 2;

  return (
    <div className={className}>
      <Heading className={titleClassName}>
        {title}
      </Heading>
      {/* One paragraph: how much audio is waiting belongs with the explanation
          of why this screen is here, not as a separate line after it. */}
      <p className={descriptionClassName}>
        {description}
        {recordedDuration && ` Er staat al ${recordedDuration} audio klaar.`}
        {` Je hebt ${optionCount} opties om verder te gaan:`}
      </p>

      <div className="space-y-3">
        {onContinue && (
          <div className={rowClassName}>
            <button
              type="button"
              onClick={onContinue}
              className={`${actionClassName} bg-brand-blauw text-brand-beige hover:bg-brand-blauw/90`}
            >
              <Play className="h-5 w-5 shrink-0" aria-hidden="true" />
              {continueLabel}
            </button>
            <p className={explainerClassName}>
              De opname gaat verder waar je gebleven was.
            </p>
          </div>
        )}

        <div className={rowClassName}>
          <button
            type="button"
            onClick={onRestore}
            className={`${actionClassName} border-2 border-brand-blauw bg-white text-brand-blauw hover:bg-brand-blauw/10`}
          >
            <Send className="h-5 w-5 shrink-0" aria-hidden="true" />
            {restoreLabel}
          </button>
          <p className={explainerClassName}>
            De opname wordt verstuurd en verwerkt tot een samenvatting.
          </p>
        </div>

        <div className={rowClassName}>
          <button
            type="button"
            onClick={() => setIsConfirmingDiscard(true)}
            className={`${actionClassName} border-2 border-brand-red bg-white text-brand-red hover:bg-brand-red hover:text-brand-beige`}
          >
            <RotateCcw className="h-5 w-5 shrink-0" aria-hidden="true" />
            {discardLabel}
          </button>
          <p className={explainerClassName}>
            De opname op dit apparaat wordt definitief verwijderd en je begint
            een nieuwe opname.
          </p>
        </div>
      </div>

      {/* Discarding is the only irreversible choice here, and the audio only
          exists in this browser: there is nothing to restore it from. */}
      {isConfirmingDiscard && (
        <ConfirmDialog
          title="Opnieuw beginnen?"
          message={
            recordedDuration
              ? `Met deze actie wordt de opname van ${recordedDuration} definitief verwijderd. Weet je zeker dat je opnieuw wilt beginnen?`
              : 'Met deze actie wordt de opname definitief verwijderd. Weet je zeker dat je opnieuw wilt beginnen?'
          }
          confirmLabel="Ja, verwijder de opname"
          cancelLabel="Nee, ga terug"
          onConfirm={() => {
            setIsConfirmingDiscard(false);
            onDiscard();
          }}
          onCancel={() => setIsConfirmingDiscard(false)}
        />
      )}
    </div>
  );
};

export default BrowserLocalRecordingRecoveryPrompt;
